import { useState, type ReactNode } from 'react';
import { usePwaInstall } from '@shared/pwa';
import { InstallPromptCard } from './install-prompt-card';

export interface InstallPromptProps {
  /** app 名稱，會出現在「將 ⋯ 加入主畫面」裡 */
  appName: string;
  /** app 圖示。省略時只顯示文字 */
  icon?: ReactNode;
  /**
   * 位置覆寫。預設貼齊視窗底部；有底部操作列或浮動按鈕的畫面要自行讓開
   * （例如 diet 傳 `bottom-[calc(5.5rem+env(safe-area-inset-bottom))]` 把卡片抬到
   * 記錄鍵上方）。
   */
  className?: string;
}

/**
 * 安裝到主畫面的提示。
 *
 * 已安裝、非手機、或使用者關掉之後都不顯示——三種情況都直接回 `null`，呼叫端
 * 不需要自己判斷要不要渲染。
 *
 * **Android 給按鈕，iOS 給步驟。**Chromium 系列會在條件符合時送出
 * `beforeinstallprompt`，我們攔下來換成自己的按鈕（見 `usePwaInstall`）；iOS 的
 * WebKit 沒有這個 API，任何瀏覽器都只能引導使用者走「分享 → 加入主畫面」。
 * 靜默安裝在所有平台都做不到，那是瀏覽器刻意的限制。
 *
 * @example
 * <InstallPrompt appName="方形照片" icon={<ImageIcon size={22} />} />
 */
export function InstallPrompt({
  appName,
  icon,
  className,
}: InstallPromptProps) {
  const { isInstalled, platform, iosBrowser, canPrompt, promptInstall } =
    usePwaInstall();
  const [isDismissed, setIsDismissed] = useState(false);

  /**
   * 按下安裝之後就收起卡片，**兩種結果都收**。
   *
   * 接受了：`appinstalled` 會讓 `isInstalled` 翻轉，但那是非同步的，中間這段時間
   * 卡片會因為事件已用掉（`canPrompt` 變 false）而改口顯示「點右上角選單⋯」——
   * 剛按完安裝卻被教怎麼手動安裝，讀起來像是失敗了。
   *
   * 婉拒了：他剛剛才對系統的確認框說不要，這時再貼一段手動步驟只是纏著他。
   */
  const handleInstall = async () => {
    await promptInstall();
    setIsDismissed(true);
  };

  /* 桌機不提示：這兩個 app 的價值（離線、主畫面圖示、全螢幕）都是手機情境 */
  if (isInstalled || isDismissed || platform === 'other') return null;

  return (
    <InstallPromptCard
      appName={appName}
      icon={icon}
      canPrompt={canPrompt}
      platform={platform}
      iosBrowser={iosBrowser}
      onInstall={() => void handleInstall()}
      onDismiss={() => setIsDismissed(true)}
      className={className}
    />
  );
}
